/* ============================================================
   NoirClave Éclat — shared motion engine (Track B)
   Progressive enhancement. Guards: prefers-reduced-motion + touch.
   Attaches by class/data hooks so section markup is untouched.
   ============================================================ */
(function () {
  'use strict';
  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var TOUCH = window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches;

  function roots() { return Array.prototype.slice.call(document.querySelectorAll('.nc-eclat')); }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // ---- Scroll reveal (fail-safe) ----------------------------------------
  var REVEAL_SEL = [
    '.eyebrow', 'h2', '.faq-item', '.r-card', '.rev-summary', '.m-card',
    '.c-card', '.p-card', '.pick-card', '.ebook-card', '.guarantee-stats',
    '.diff-sec .diff-wrap', '.impact-sec .stat-num', '.bundle-card'
  ].join(',');

  function setupReveal(root) {
    if (REDUCE || !('IntersectionObserver' in window)) return;
    root.classList.add('nc-js');
    var els = Array.prototype.slice.call(root.querySelectorAll(REVEAL_SEL));
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e, i) {
        if (e.isIntersecting) {
          var el = e.target;
          setTimeout(function () { el.classList.add('nc-in'); }, Math.min(i, 6) * 60);
          io.unobserve(el);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { el.classList.add('nc-reveal'); io.observe(el); });
  }

  // ---- Counter animation -------------------------------------------------
  function animateCount(el) {
    var raw = (el.getAttribute('data-nc-count') || el.textContent || '').trim();
    var m = raw.match(/-?\d[\d,]*\.?\d*/);
    if (!m) return;
    var target = parseFloat(m[0].replace(/,/g, ''));
    if (!isFinite(target)) return;
    var prefix = raw.slice(0, m.index);
    var suffix = raw.slice(m.index + m[0].length);
    var decimals = (m[0].split('.')[1] || '').length;
    var hasComma = m[0].indexOf(',') !== -1;
    var start = null, dur = 1400;
    function fmt(v) {
      var s = v.toFixed(decimals);
      if (hasComma) s = Number(s).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      return prefix + s + suffix;
    }
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt(target * eased);
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = fmt(target);
    }
    requestAnimationFrame(step);
  }

  function setupCounters(root) {
    if (REDUCE || !('IntersectionObserver' in window)) return;
    var els = Array.prototype.slice.call(
      root.querySelectorAll('.big-n, .guar-num, .impact-sec .stat-num, [data-nc-count]')
    );
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { animateCount(e.target); io.unobserve(e.target); }
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  // ---- Review bars fill on scroll ---------------------------------------
  function setupBars(root) {
    if (!('IntersectionObserver' in window)) return;
    var bars = Array.prototype.slice.call(root.querySelectorAll('.bar-fill'));
    if (!bars.length) return;
    bars.forEach(function (b) {
      b.setAttribute('data-nc-w', b.style.width || getComputedStyle(b).width);
      if (!REDUCE) b.style.width = '0%';
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          var b = e.target;
          requestAnimationFrame(function () { b.style.width = b.getAttribute('data-nc-w'); });
          io.unobserve(b);
        }
      });
    }, { threshold: 0.3 });
    bars.forEach(function (b) { io.observe(b); });
  }

  // ---- Mouse spotlight ---------------------------------------------------
  function setupSpotlight(root) {
    if (TOUCH || REDUCE) return;
    var els = Array.prototype.slice.call(root.querySelectorAll('[data-gal-wrap], .nc-spotlight'));
    els.forEach(function (el) {
      el.classList.add('nc-spotlight');
      el.addEventListener('mousemove', function (ev) {
        var r = el.getBoundingClientRect();
        el.style.setProperty('--mx', ((ev.clientX - r.left) / r.width * 100) + '%');
        el.style.setProperty('--my', ((ev.clientY - r.top) / r.height * 100) + '%');
        el.classList.add('nc-hot');
      });
      el.addEventListener('mouseleave', function () { el.classList.remove('nc-hot'); });
    });
  }

  // ---- 3D tilt (opt-in) --------------------------------------------------
  function setupTilt(root) {
    if (TOUCH || REDUCE) return;
    var els = Array.prototype.slice.call(root.querySelectorAll('[data-nc-tilt]'));
    var MAX = 3;
    els.forEach(function (el) {
      el.addEventListener('mousemove', function (ev) {
        var r = el.getBoundingClientRect();
        var rx = (((ev.clientY - r.top) / r.height) - 0.5) * -2 * MAX;
        var ry = (((ev.clientX - r.left) / r.width) - 0.5) * 2 * MAX;
        el.style.transform = 'perspective(700px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg)';
      });
      el.addEventListener('mouseleave', function () { el.style.transform = ''; });
    });
  }

  ready(function () {
    roots().forEach(function (root) {
      try { setupReveal(root); } catch (e) {}
      try { setupCounters(root); } catch (e) {}
      try { setupBars(root); } catch (e) {}
      try { setupSpotlight(root); } catch (e) {}
      try { setupTilt(root); } catch (e) {}
    });
  });
})();
